import { createElement } from "react";
/**
 * External dependencies
 */
import clsx from 'clsx';
// Import CompositeStore type, which is not exported from @wordpress/components.
// eslint-disable-next-line no-restricted-imports

/**
 * WordPress dependencies
 */
import { useInstanceId } from '@wordpress/compose';
import { __experimentalHStack as HStack, __experimentalVStack as VStack, Button, privateApis as componentsPrivateApis, Spinner, VisuallyHidden } from '@wordpress/components';
import { useCallback, useEffect, useMemo, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { moreVertical } from '@wordpress/icons';

/**
 * Internal dependencies
 */
import { unlock } from './lock-unlock';
import { ActionsDropdownMenuGroup, ActionModal } from './item-actions';
const {
  useCompositeStoreV2: useCompositeStore,
  CompositeV2: Composite,
  CompositeItemV2: CompositeItem,
  CompositeRowV2: CompositeRow,
  DropdownMenuV2: DropdownMenu
} = unlock(componentsPrivateApis);
function ListItem({
  actions,
  id,
  isSelected,
  item,
  mediaField,
  onSelect,
  primaryField,
  store,
  visibleFields
}) {
  const itemRef = useRef(null);
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  useEffect(() => {
    if (isSelected) {
      itemRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [isSelected]);
  const {
    primaryAction,
    eligibleActions
  } = useMemo(() => {
    // If an action is eligible for all items, doesn't need
    // to provide the `isEligible` function.
    const _eligibleActions = actions.filter(action => !action.isEligible || action.isEligible(item));
    const _primaryActions = _eligibleActions.filter(action => action.isPrimary && !!action.icon);
    return {
      primaryAction: _primaryActions?.[0],
      eligibleActions: _eligibleActions
    };
  }, [actions, item]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  return createElement(CompositeRow, {
    ref: itemRef,
    render: createElement("li", null),
    role: "row",
    className: clsx({
      'is-selected': isSelected,
      'is-hovered': isHovered
    }),
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave
  }, createElement(HStack, {
    className: "dataviews-view-list__item-wrapper",
    alignment: "top"
  }, createElement("div", {
    role: "gridcell"
  }, createElement(CompositeItem, {
    store: store,
    render: createElement("div", null),
    role: "button",
    id: id,
    "aria-pressed": isSelected,
    "aria-labelledby": labelId,
    "aria-describedby": descriptionId,
    className: "dataviews-view-list__item",
    onClick: () => onSelect(item)
  }, createElement(HStack, {
    spacing: 3,
    justify: "start",
    alignment: "flex-start"
  }, createElement("div", {
    className: "dataviews-view-list__media-wrapper"
  }, mediaField?.render({
    item
  }) || createElement("div", {
    className: "dataviews-view-list__media-placeholder"
  })), createElement(VStack, {
    spacing: 1
  }, createElement("span", {
    className: "dataviews-view-list__primary-field",
    id: labelId
  }, primaryField?.render({
    item
  })), createElement("div", {
    className: "dataviews-view-list__fields",
    id: descriptionId
  }, visibleFields.map(field => createElement("div", {
    key: field.id,
    className: "dataviews-view-list__field"
  }, createElement(VisuallyHidden, {
    as: "span",
    className: "dataviews-view-list__field-label"
  }, field.header), createElement("span", {
    className: "dataviews-view-list__field-value"
  }, field.render({
    item
  }))))))))), actions?.length > 0 && createElement(HStack, {
    spacing: 1,
    justify: "flex-end",
    className: "dataviews-view-list__item-actions",
    style: {
      flexShrink: '0',
      width: 'auto'
    }
  }, primaryAction && 'RenderModal' in primaryAction && createElement("div", {
    role: "gridcell"
  }, createElement(CompositeItem, {
    store: store,
    render: createElement(Button, {
      label: primaryAction.label,
      icon: primaryAction.icon,
      isDestructive: primaryAction.isDestructive,
      size: "compact",
      onClick: () => setIsModalOpen(true)
    })
  }, isModalOpen && createElement(ActionModal, {
    action: primaryAction,
    items: [item],
    closeModal: () => setIsModalOpen(false)
  }))), primaryAction && !('RenderModal' in primaryAction) && createElement("div", {
    role: "gridcell",
    key: primaryAction.id
  }, createElement(CompositeItem, {
    store: store,
    render: createElement(Button, {
      label: primaryAction.label,
      icon: primaryAction.icon,
      isDestructive: primaryAction.isDestructive,
      size: "compact",
      onClick: () => primaryAction.callback([item])
    })
  })), createElement("div", {
    role: "gridcell"
  }, createElement(DropdownMenu, {
    trigger: createElement(CompositeItem, {
      store: store,
      render: createElement(Button, {
        size: "compact",
        icon: moreVertical,
        label: __('Actions'),
        disabled: !actions.length,
        onKeyDown: event => {
          if (event.key === 'ArrowDown') {
            // Prevent the default behaviour (open dropdown menu) and go down.
            event.preventDefault();
            store.move(store.down());
          }
          if (event.key === 'ArrowUp') {
            // Prevent the default behavior (open dropdown menu) and go up.
            event.preventDefault();
            store.move(store.up());
          }
        }
      })
    }),
    placement: "bottom-end"
  }, createElement(ActionsDropdownMenuGroup, {
    actions: eligibleActions,
    item: item
  }))))));
}
export default function ViewList(props) {
  const {
    actions,
    data,
    fields,
    getItemId,
    isLoading,
    onSelectionChange,
    selection,
    view
  } = props;
  const baseId = useInstanceId(ViewList, 'view-list');
  const selectedItem = data?.findLast(item => selection.includes(item.id));
  const mediaField = fields.find(field => field.id === view.layout.mediaField);
  const primaryField = fields.find(field => field.id === view.layout.primaryField);
  const visibleFields = fields.filter(field => !view.hiddenFields.includes(field.id) && ![view.layout.primaryField, view.layout.mediaField].includes(field.id));
  const onSelect = useCallback(item => onSelectionChange([item]), [onSelectionChange]);
  const getItemDomId = useCallback(item => item ? `${baseId}-${getItemId(item)}` : undefined, [baseId, getItemId]);
  const store = useCompositeStore({
    defaultActiveId: getItemDomId(selectedItem)
  });

  // Manage focused item, when the active one is removed from the list.
  const isActiveIdInList = store.useState(state => state.items.some(item => item.id === state.activeId));
  useEffect(() => {
    if (!isActiveIdInList) {
      // Prefer going down, except if there is no item below (last item), then go up (last item in list).
      if (store.down()) {
        store.move(store.down());
      } else if (store.up()) {
        store.move(store.up());
      }
    }
  }, [isActiveIdInList]);
  const hasData = data?.length;
  if (!hasData) {
    return createElement("div", {
      className: clsx({
        'dataviews-loading': isLoading,
        'dataviews-no-results': !hasData && !isLoading
      })
    }, !hasData && createElement("p", null, isLoading ? createElement(Spinner, null) : __('No results')));
  }
  return createElement(Composite, {
    id: baseId,
    render: createElement("ul", null),
    className: "dataviews-view-list",
    role: "grid",
    store: store
  }, data.map(item => {
    const id = getItemDomId(item);
    return createElement(ListItem, {
      key: id,
      id: id,
      actions: actions,
      item: item,
      isSelected: item === selectedItem,
      onSelect: onSelect,
      mediaField: mediaField,
      primaryField: primaryField,
      store: store,
      visibleFields: visibleFields
    });
  }));
}
//# sourceMappingURL=view-list.js.map